/**
 * Header — full-width поверх кино-hero (референс «Honey Flight»):
 * справа навигация + язык + золотая CTA. Логотип слева СКРЫТ, пока
 * гигантский CAFÉ OS hero виден по центру (иначе дубль выскакивает
 * в углу при первой загрузке — фидбек владельца 2026-06-11);
 * появляется плавно, когда hero ушёл за скролл.
 * Прозрачный наверху страницы (видео дышит), при скролле — тёмное стекло.
 */
import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useLang } from "../i18n";
import { LangSwitcher } from "./LangSwitcher";

const LINKS: { href: string; key: "apps" | "features" | "caseStudy" | "pricing" | "faq" }[] = [
  { href: "#apps", key: "apps" },
  { href: "#features", key: "features" },
  { href: "#case", key: "caseStudy" },
  { href: "#pricing", key: "pricing" },
  { href: "#faq", key: "faq" },
];

export function Header() {
  const { t, lang } = useLang();
  const deckHref = lang === "kk" ? "/landing/deck/kz/index.html" : "/landing/deck/index.html";
  const [scrolled, setScrolled] = useState(false);
  const [logoVisible, setLogoVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      // Лого появляется только когда центральный заголовок hero ушёл с экрана
      setLogoVisible(window.scrollY > window.innerHeight * 0.6);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className="hero-header fixed inset-x-0 top-0 z-[90]"
      style={{
        background: scrolled ? "rgba(8, 7, 6, 0.78)" : "transparent",
        backdropFilter: scrolled ? "blur(20px)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(20px)" : "none",
        borderBottom: scrolled ? "1px solid var(--line)" : "1px solid transparent",
        transition: "background 400ms ease, border-color 400ms ease",
      }}
    >
      <div className="mx-auto flex w-full max-w-[1480px] items-center justify-between gap-3 px-4 py-4 sm:gap-4 sm:px-8">
        <a
          href="#top"
          className="t-brand whitespace-nowrap text-base tracking-wide"
          style={{
            color: "var(--fg)",
            opacity: logoVisible ? 1 : 0,
            transform: logoVisible ? "translateY(0)" : "translateY(-8px)",
            pointerEvents: logoVisible ? "auto" : "none",
            transition: "opacity 500ms ease, transform 500ms ease",
          }}
          aria-hidden={!logoVisible}
          tabIndex={logoVisible ? 0 : -1}
        >
          ORDA<span style={{ color: "var(--gold)" }}>&nbsp;OS</span>
        </a>

        <div className="flex items-center gap-2 sm:gap-3">
          <nav className="hidden items-center gap-1 lg:flex" aria-label="Основная навигация">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="rounded-full px-3.5 py-2 text-[12px] font-bold uppercase tracking-[0.14em] transition-colors"
                style={{ color: "var(--fg-soft)" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--fg)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--fg-soft)")}
              >
                {t.nav[l.key]}
              </a>
            ))}
            <a
              href={deckHref}
              target="_blank"
              rel="noopener"
              className="rounded-full px-3.5 py-2 text-[12px] font-bold uppercase tracking-[0.14em] transition-colors"
              style={{ color: "var(--gold)" }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.75")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            >
              {t.nav.deck}
            </a>
          </nav>

          <LangSwitcher />
          <a href="#lead" className="btn-gold !py-2.5 !px-3.5 sm:!px-5 whitespace-nowrap">
            <span className="hidden sm:inline">{t.nav.cta}</span>
            <span className="sm:hidden">{t.nav.ctaShort}</span>
            <ArrowRight className="h-4 w-4" aria-hidden />
          </a>
        </div>
      </div>
    </header>
  );
}
